import { useEffect, useRef, useState, type ReactNode } from 'react'
import { fill, COMPONENTS_LIST_PANEL_TEXTS as T } from '../../shared/texts'

// The table block of a list view. A header row shows the element count on the
// left ("x Elemente", or "x von y Elemente" while a search/filter narrows the
// list) and the search toggle (+ optional filter toggle) on the right. When
// `filters` are provided a filter button appears and a filter block slides open
// below the header. The table itself is passed as children.

interface SearchProps {
    value: string
    onChange: (v: string) => void
    placeholder?: string
}

interface Props {
    count: number       // elements after search/filter
    grandTotal: number  // total elements without any search/filter
    filtered: boolean   // a search or filter is currently narrowing the list
    search?: SearchProps
    columnManager?: ReactNode // column visibility control, shown next to search
    filters?: ReactNode // present → filter button + slide-down block
    children: ReactNode
}

function MagnifierIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    )
}

function FunnelIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
    )
}

function countLabel(count: number, grandTotal: number, filtered: boolean) {
    const noun = (filtered ? count : grandTotal) === 1 ? T.elementOne : T.elementMany
    return filtered ? fill(T.countFiltered, { count, grandTotal, noun }) : fill(T.countAll, { grandTotal, noun })
}

export default function ListPanel({ count, grandTotal, filtered, search, columnManager, filters, children }: Props) {
    const [searchOpen, setSearchOpen] = useState(false)
    const [filterOpen, setFilterOpen] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => { if (searchOpen) inputRef.current?.focus() }, [searchOpen])

    function toggleSearch() {
        // Closing a non-empty search clears it, so the list returns to all rows.
        if (searchOpen && search?.value) search.onChange('')
        setSearchOpen((o) => !o)
    }

    return (
        <div className="panel list-panel">
            <div className="list-panel-head">
                <div className="list-count">{countLabel(count, grandTotal, filtered)}</div>
                <div className="row">
                    {search && (
                        <div className={`list-search ${searchOpen ? 'open' : ''}`}>
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder={search.placeholder}
                                value={search.value}
                                onChange={(e) => search.onChange(e.target.value)}
                            />
                            <button className="icon-btn" onClick={toggleSearch} title={T.search} aria-label={T.search}>
                                <MagnifierIcon />
                            </button>
                        </div>
                    )}
                    {filters && (
                        <button
                            className={`icon-btn ${filterOpen ? 'active' : ''}`}
                            onClick={() => setFilterOpen((o) => !o)}
                            title={T.filter} aria-label={T.filter} aria-expanded={filterOpen}
                        >
                            <FunnelIcon />
                        </button>
                    )}
                    {columnManager}
                </div>
            </div>

            {filters && (
                <div className={`filter-panel ${filterOpen ? 'open' : ''}`}>
                    <div className="filter-panel-inner">{filters}</div>
                </div>
            )}

            <div className="list-panel-body">{children}</div>
        </div>
    )
}
